import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ReferralState, Sport } from '__generated__/globalTypes';
import { SBHorizontal, Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';
import { range } from 'lib/arrays';
import { CARDS_REQUIREMENTS_BY_SPORT } from 'lib/referral';
import { getFormattedSport } from 'lib/sports';

import { InProgressReferralBar_Referral } from './__generated__/index.graphql';

const ProgressBar = styled.div`
  display: flex;
  flex: 1;
  gap: 2px;

  & > span {
    height: var(--half-unit);
    background-color: var(--c-nd-200);
    flex: 1;
    border-radius: 1px;
    &.active {
      background-color: var(--c-green-600);
    }
  }
`;

type SportProps = { sport: Sport; overrideMax?: number | null; count: number };

const SportRefereeProgression = ({ sport, overrideMax, count }: SportProps) => {
  const max = overrideMax || CARDS_REQUIREMENTS_BY_SPORT[sport];

  return (
    <Vertical gap={0.5}>
      <SBHorizontal>
        <BodyM as="p">
          <FormattedMessage
            id="RefereeProgression.SportRefereeProgression.sportCardsCollected"
            defaultMessage="{sport} {count, plural, =0 {card} one {card} other {cards}} collected"
            values={{
              count,
              sport: getFormattedSport(sport),
            }}
          />
        </BodyM>
        <BodyM as="p">
          {count} / {max}
        </BodyM>
      </SBHorizontal>
      <ProgressBar>
        {range(max).map((x, i) => {
          return <span key={x} className={classNames({ active: i < count })} />;
        })}
      </ProgressBar>
    </Vertical>
  );
};

type Props = {
  referral: InProgressReferralBar_Referral;
  sport: Sport;
};

export const InProgressReferralBar = ({ referral, sport }: Props) => {
  return (
    <Vertical gap={2}>
      {ReferralState.PENDING_REVIEW === referral.state && (
        <FormattedMessage
          id="InProgressReferralBar.pendingReview"
          defaultMessage="Your referral is under review..."
        />
      )}
      <SportRefereeProgression
        sport={sport}
        count={referral.refereeSportCardsBoughtFromPrimaryMarketCount}
      />
    </Vertical>
  );
};

InProgressReferralBar.fragments = {
  referral: gql`
    fragment InProgressReferralBar_Referral on Referral {
      id
      state
      refereeSportCardsBoughtFromPrimaryMarketCount(sport: $sport)
    }
  ` as TypedDocumentNode<InProgressReferralBar_Referral>,
};
